import clsx from 'clsx';
import { type MouseEventHandler } from 'react';

/**
 * `<Chip>` — single primitive serving BOTH inline-in-prose `<ProvenanceChip>`
 * (Session 5) AND panel-side pills (Phase 3) per pre-spec-notes.md Decision 6.
 * One `<Chip>` family, two consumers; one place to maintain visual consistency.
 *
 * Mono, 2px radius, dot+label+optional value. 5 status variants:
 * `user | market | system | agent | invalid`. Single-letter glyph for
 * a11y (color-blind users see the letter even when the dot color is
 * indistinguishable).
 *
 * The `invalid` variant is consumed by `<DeepLink>` (Session 5) when the
 * link target doesn't resolve at render time per I6 — no dot, no chevron,
 * `cursor: not-allowed`, `aria-disabled="true"`, click is a no-op.
 *
 * The `agent` variant uses `bg-panel-bg` + `border-purple/20` because
 * `--color-purple-dim` is not yet defined in `index.css` (only `--color-purple`
 * ships today; other semantic colors carry a `-dim` sibling). Documented
 * fallback per blueprint table.
 */
export type ChipVariant = 'user' | 'market' | 'system' | 'agent' | 'invalid';

export interface ChipProps {
  variant: ChipVariant;
  label: string;
  value?: string;
  className?: string;
  /**
   * Composing components (ProvenanceChip, DeepLink) bind click + popover
   * semantics. The Chip itself is presentational. `onClick` is swallowed
   * for `variant="invalid"` regardless of whether `interactive` is true.
   */
  onClick?: MouseEventHandler<HTMLElement>;
  /**
   * `true` renders as `<button type="button">`; `false` (default) renders as
   * `<span>`. ProvenanceChip uses interactive (clickable); panel-side status
   * pills use non-interactive (decorative).
   */
  interactive?: boolean;
}

interface VariantTokens {
  /** Tailwind class for the leading dot. Empty string for `invalid` (no dot). */
  dot: string;
  /** Tailwind class for the chip background. */
  bg: string;
  /** Tailwind class for the chip border. */
  border: string;
  /** Tailwind class for label/value text color. */
  text: string;
  /** Single-letter a11y glyph mirroring the color cue. */
  glyph: string;
}

const VARIANT_TOKENS: Record<ChipVariant, VariantTokens> = {
  user: {
    dot: 'bg-accent',
    bg: 'bg-accent-dim',
    border: 'border-accent/15',
    text: 'text-accent',
    glyph: 'U',
  },
  market: {
    dot: 'bg-warn',
    bg: 'bg-warn-dim',
    border: 'border-warn/15',
    text: 'text-warn',
    glyph: 'M',
  },
  system: {
    dot: 'bg-text-muted',
    bg: 'bg-panel-bg',
    border: 'border-panel-border',
    text: 'text-text-muted',
    glyph: 'S',
  },
  agent: {
    dot: 'bg-purple',
    // No bg-purple-dim token in index.css — fall back to panel bg + slightly
    // heavier border per blueprint table. If/when --color-purple-dim is
    // added to the design system, swap `bg` to `bg-purple-dim` and `border`
    // back to `border-purple/15`.
    bg: 'bg-panel-bg',
    border: 'border-purple/20',
    text: 'text-purple',
    glyph: 'A',
  },
  invalid: {
    dot: '',
    bg: 'bg-panel-bg',
    border: 'border-panel-border',
    text: 'text-text-dim',
    glyph: '!',
  },
};

export function Chip({
  variant,
  label,
  value,
  className,
  onClick,
  interactive = false,
}: ChipProps) {
  const tokens = VARIANT_TOKENS[variant];
  const isInvalid = variant === 'invalid';

  const baseClasses = clsx(
    'inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-[2px] border',
    'font-mono text-micro tracking-[0.3px]',
    tokens.bg,
    tokens.border,
    tokens.text,
    isInvalid && 'cursor-not-allowed',
    className,
  );

  const inner = (
    <>
      {!isInvalid && tokens.dot && (
        <span
          className={clsx('w-1.5 h-1.5 rounded-full shrink-0', tokens.dot)}
          aria-hidden="true"
        />
      )}
      <span className="font-semibold" aria-label={`${variant} source`}>
        {tokens.glyph}
      </span>
      <span>{label}</span>
      {value && <span className="text-text font-mono tabular-nums">{value}</span>}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        data-chip-variant={variant}
        onClick={isInvalid ? undefined : onClick}
        disabled={isInvalid}
        aria-disabled={isInvalid || undefined}
        className={baseClasses}
      >
        {inner}
      </button>
    );
  }

  return (
    <span
      data-chip-variant={variant}
      onClick={isInvalid ? undefined : onClick}
      aria-disabled={isInvalid || undefined}
      className={baseClasses}
    >
      {inner}
    </span>
  );
}
